'use client'

/**
 * Error Details Modal
 *
 * Issue #1793 - Shows detailed error information with integrated bug reporting.
 * Displays error message, stack trace (collapsible), timestamp, and page URL.
 * Includes"Report Bug" button that opens FeedbackModal with pre-filled data.
 */

import { useState, useEffect, useCallback, useId } from 'react'
import { toast, toastError } from '@/lib/toast'
import {
  AlertTriangle,
  Copy,
  Bug,
  Clock,
  ExternalLink,
} from '@/components/icons'
import { DisclosureChevron } from '@/components/ui/disclosure-chevron'
import { Modal } from '@/components/ui/modal'
import type { ErrorDetails } from '@/lib/error-types'

type ErrorDetailsModalProps = {
  isOpen: boolean
  onClose: () => void
  errorDetails: ErrorDetails | null
  onReportBug: (errorDetails: ErrorDetails) => void
}

export function ErrorDetailsModal({
  isOpen,
  onClose,
  errorDetails,
  onReportBug,
}: ErrorDetailsModalProps) {
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false)
  const technicalId = useId()

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowTechnicalDetails(false)
    }
  }, [isOpen])

  const handleCopyToClipboard = useCallback(async () => {
    if (!errorDetails) return

    const lines = [
      `Error: ${errorDetails.message}`,
      `Page: ${errorDetails.pageUrl}`,
      `Time: ${errorDetails.timestamp.toISOString()}`,
    ]

    if (errorDetails.statusCode) {
      lines.push(`Status Code: ${errorDetails.statusCode}`)
    }

    if (errorDetails.requestUrl) {
      lines.push(`Request URL: ${errorDetails.requestUrl}`)
    }

    if (errorDetails.digest) {
      lines.push(`Digest: ${errorDetails.digest}`)
    }

    if (errorDetails.stack) {
      lines.push('', 'Stack Trace:', errorDetails.stack)
    }

    if (errorDetails.context) {
      lines.push('', 'Context:', JSON.stringify(errorDetails.context, null, 2))
    }

    // navigator.clipboard.writeText rejects on permission-denied or in
    // browsers/contexts without the Clipboard API; the catch is the
    // user-facing fallback (toast) rather than a swallowed error, so a
    // structured client-error report would be redundant noise.
    try {
      await navigator.clipboard.writeText(lines.join('\n'))
      toast.success('Error details copied to clipboard')
    } catch {
      // Best-effort error-details display; toast is shown regardless — see sub-issue R carve-out
      toastError({
        message: 'Failed to copy to clipboard',
        context: { action: 'copyToClipboard' },
      })
    }
  }, [errorDetails])

  const handleReportBug = useCallback(() => {
    if (errorDetails) {
      onReportBug(errorDetails)
    }
  }, [errorDetails, onReportBug])

  if (!errorDetails) return null

  const formattedTime = errorDetails.timestamp.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })

  const hasTechnicalDetails = !!(
    errorDetails.stack ||
    errorDetails.context ||
    errorDetails.statusCode ||
    errorDetails.requestUrl ||
    errorDetails.digest
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabelledBy="error-details-modal-title"
      size="md"
      noPadding
      footer={
        <>
          <button
            type="button"
            onClick={handleCopyToClipboard}
            className="px-3 py-2 text-sm font-medium text-text-primary hover:bg-fill-10 rounded-md transition-colors flex items-center gap-1.5"
          >
            <Copy className="w-4 h-4" />
            Copy Details
          </button>
          <button
            type="button"
            onClick={handleReportBug}
            className="px-4 py-2 text-sm font-medium text-text-primary-reversed bg-status-red-bg-emphasis hover:bg-status-red-bg-emphasis-hover rounded-md transition-colors flex items-center gap-1.5"
          >
            <Bug className="w-4 h-4" />
            Report Bug
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-text-primary hover:bg-fill-10 rounded-md transition-colors"
          >
            Close
          </button>
        </>
      }
    >
      {/* Custom Header (with AlertTriangle icon) */}
      <div className="flex items-center gap-2 p-4 border-b border-stroke-regular">
        <AlertTriangle className="w-5 h-5 text-status-red" aria-hidden="true" />
        <h2
          id="error-details-modal-title"
          className="text-lg font-bold text-text-primary"
        >
          Error Details
        </h2>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Error Message */}
        <div>
          <h3 className="text-sm font-medium text-text-secondary mb-1">
            Error
          </h3>
          <p className="text-text-primary font-medium">
            {errorDetails.message}
          </p>
        </div>

        {/* Technical Details (Collapsible) */}
        {hasTechnicalDetails && (
          <div className="border border-stroke-regular rounded-lg overflow-hidden">
            <button
              aria-controls={showTechnicalDetails ? technicalId : undefined}
              type="button"
              onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
              className="w-full flex items-center justify-between p-3 bg-surface-panel hover:bg-fill-10 transition-colors text-left"
              aria-expanded={showTechnicalDetails}
            >
              <span className="text-sm font-medium text-text-primary">
                Technical Details
              </span>
              <DisclosureChevron
                open={showTechnicalDetails}
                className="w-4 h-4 text-text-secondary"
              />
            </button>

            {showTechnicalDetails && (
              <div
                id={technicalId}
                className="p-3 border-t border-stroke-regular space-y-3"
              >
                {/* Stack Trace */}
                {errorDetails.stack && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                        Stack Trace
                      </span>
                      <button
                        type="button"
                        onClick={handleCopyToClipboard}
                        className="flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary transition-colors"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-3 h-3" />
                        Copy
                      </button>
                    </div>
                    <pre className="text-xs text-text-secondary bg-fill-10 rounded p-2 overflow-x-auto max-h-48 whitespace-pre-wrap break-words font-mono">
                      {errorDetails.stack}
                    </pre>
                  </div>
                )}

                {/* Context */}
                {errorDetails.context && (
                  <div>
                    <span className="text-xs font-medium text-text-secondary uppercase tracking-wide block mb-1">
                      Context
                    </span>
                    <pre className="text-xs text-text-secondary bg-fill-10 rounded p-2 overflow-x-auto max-h-32 whitespace-pre-wrap break-words font-mono">
                      {JSON.stringify(errorDetails.context, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Status Code */}
                {errorDetails.statusCode && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-text-secondary">
                      Status Code:
                    </span>
                    <span className="text-xs text-text-primary font-mono bg-fill-10 px-1.5 py-0.5 rounded">
                      {errorDetails.statusCode}
                    </span>
                  </div>
                )}

                {/* Request URL */}
                {errorDetails.requestUrl && (
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-medium text-text-secondary whitespace-nowrap">
                      Request URL:
                    </span>
                    <span className="text-xs text-text-primary font-mono bg-fill-10 px-1.5 py-0.5 rounded break-all">
                      {errorDetails.requestUrl}
                    </span>
                  </div>
                )}

                {/* Digest */}
                {errorDetails.digest && (
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-medium text-text-secondary shrink-0">
                      Digest:
                    </span>
                    <span className="text-xs text-text-primary font-mono bg-fill-10 px-1.5 py-0.5 rounded break-all">
                      {errorDetails.digest}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Metadata */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-text-secondary">
          <div className="flex items-center gap-1.5">
            <ExternalLink className="w-4 h-4" />
            <span className="truncate max-w-[200px]">
              {errorDetails.pageUrl || '/'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            <span>{formattedTime}</span>
          </div>
        </div>
      </div>
    </Modal>
  )
}
